import React from 'react'
import {
  Home,
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
  Target,
  Calendar,
  MapPin,
  BarChart3
} from 'lucide-react'
import { PropertyResponse, apiClient } from '@/services/api'
import { PortfolioAnalytics } from '@/types/portfolio'
import { getRenovationCosts, calculateMonthlyPayment, safeNumber } from '@/utils/propertyCalculations'

interface PortfolioStatsProps {
  properties: PropertyResponse[]
  loading?: boolean
  analytics?: PortfolioAnalytics
}

interface StatsCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  trend?: {
    value: number
    label: string
    isPositive: boolean
  }
  className?: string
}

function StatsCard({ title, value, subtitle, icon, trend, className = '' }: StatsCardProps) {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className="flex items-center mt-2">
              {trend.isPositive ? (
                <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600 mr-1" />
              )}
              <span className={`text-sm font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {trend.value > 0 ? '+' : ''}{trend.value.toFixed(1)}%
              </span>
              <span className="text-sm text-gray-500 ml-1">{trend.label}</span>
            </div>
          )}
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          {icon}
        </div>
      </div>
    </div>
  )
}

export function PortfolioStats({ properties, loading = false, analytics }: PortfolioStatsProps) {
  const [portfolioAnalytics, setPortfolioAnalytics] = React.useState<PortfolioAnalytics | null>(analytics || null);
  const [analyticsLoading, setAnalyticsLoading] = React.useState(false);

  // Fetch portfolio analytics if not provided
  React.useEffect(() => {
    if (!analytics && properties.length > 0 && !portfolioAnalytics) {
      const fetchAnalytics = async () => {
        try {
          setAnalyticsLoading(true);
          const response = await apiClient.getPortfolioAnalytics();
          if (response.success) {
            setPortfolioAnalytics(response.data);
          }
        } catch (error) {
          console.warn('Failed to load portfolio analytics:', error);
          // Continue with calculated stats as fallback
        } finally {
          setAnalyticsLoading(false);
        }
      };

      fetchAnalytics();
    }
  }, [analytics, properties.length, portfolioAnalytics]);
  const stats = React.useMemo(() => {
    // Use analytics data if available, otherwise calculate from properties
    if (portfolioAnalytics) {
      return {
        totalProperties: portfolioAnalytics.total_properties,
        totalInvestment: portfolioAnalytics.value_metrics.total_investment,
        totalCashFlow: portfolioAnalytics.value_metrics.total_monthly_cash_flow,
        averageROI: portfolioAnalytics.value_metrics.average_roi,
        totalEquity: portfolioAnalytics.value_metrics.total_equity,
        averageCapRate: portfolioAnalytics.value_metrics.average_cap_rate,
        analysisTypes: portfolioAnalytics.analysis_count_by_type,
        recentActivity: portfolioAnalytics.recent_activity.properties_added_last_30_days,
        totalAnalyses: portfolioAnalytics.total_analyses,
        propertiesWithMultipleAnalyses: portfolioAnalytics.properties_with_multiple_analyses,
        locations: new Set(Object.keys(portfolioAnalytics.geographic_distribution))
      }
    }

    if (!properties.length) {
      return {
        totalProperties: 0,
        totalInvestment: 0,
        totalCashFlow: 0,
        averageROI: 0,
        totalEquity: 0,
        averageCapRate: 0,
        analysisTypes: { 'buy-hold': 0, flip: 0, brrr: 0 },
        recentActivity: 0,
        totalAnalyses: 0,
        propertiesWithMultipleAnalyses: 0,
        locations: new Set()
      }
    }

    let totalInvestment = 0
    let totalCashFlow = 0
    let totalROI = 0
    let totalEquity = 0
    let totalCapRate = 0
    const analysisTypes = { 'buy-hold': 0, flip: 0, brrr: 0 }
    let totalAnalyses = 0
    let propertiesWithMultipleAnalyses = 0
    const locations = new Set()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    properties.forEach(property => {
      // Total investment
      const renovationCosts = getRenovationCosts(property.renovation_costs)
      const propertyInvestment = safeNumber(property.purchase_price) + renovationCosts
      totalInvestment += propertyInvestment

      // Cash flow calculation
      const monthlyMortgage = calculateMonthlyPayment(
        safeNumber(property.loan_amount),
        safeNumber(property.interest_rate),
        safeNumber(property.loan_term, 30)
      )

      const monthlyExpenses = monthlyMortgage +
        (safeNumber(property.annual_taxes) / 12) +
        (safeNumber(property.annual_insurance) / 12) +
        safeNumber(property.monthly_hoa_fees)

      const monthlyCashFlow = safeNumber(property.expected_monthly_rent) - monthlyExpenses
      totalCashFlow += monthlyCashFlow

      // Cash-on-Cash ROI
      const downPayment = safeNumber(property.down_payment)
      const cashOnCashReturn = downPayment > 0
        ? ((monthlyCashFlow * 12) / downPayment) * 100
        : 0
      totalROI += cashOnCashReturn

      // Cap rate calculation
      const noi = safeNumber(property.expected_monthly_rent) * 12 -
        safeNumber(property.annual_taxes) -
        safeNumber(property.annual_insurance) -
        safeNumber(property.monthly_hoa_fees) * 12
      const purchasePrice = safeNumber(property.purchase_price)
      const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0
      totalCapRate += capRate

      // Equity (simplified as down payment for now)
      totalEquity += downPayment

      // Analysis counts - use enhanced data if available
      const propertyAnalysisCount = property.analysis_count || 1
      totalAnalyses += propertyAnalysisCount

      if (propertyAnalysisCount > 1) {
        propertiesWithMultipleAnalyses++
      }

      // Analysis types - enhanced logic using metadata
      if (property.analysis_metadata) {
        if (property.analysis_metadata['buy-hold']) {
          analysisTypes['buy-hold'] += property.analysis_metadata['buy-hold'].count
        }
        if (property.analysis_metadata['flip']) {
          analysisTypes['flip'] += property.analysis_metadata['flip'].count
        }
        if (property.analysis_metadata['brrr']) {
          analysisTypes['brrr'] += property.analysis_metadata['brrr'].count
        }
      } else if (property.analysis_status in analysisTypes) {
        // Fallback to old method
        analysisTypes[property.analysis_status as keyof typeof analysisTypes]++
      }

      // Locations
      if (property.city && property.state) {
        locations.add(`${property.city}, ${property.state}`)
      }
    })

    return {
      totalProperties: properties.length,
      totalInvestment,
      totalCashFlow,
      averageROI: properties.length > 0 ? totalROI / properties.length : 0,
      totalEquity,
      averageCapRate: properties.length > 0 ? totalCapRate / properties.length : 0,
      analysisTypes,
      recentActivity: properties.filter(p => new Date(p.created_at) > thirtyDaysAgo).length,
      totalAnalyses,
      propertiesWithMultipleAnalyses,
      locations
    }
  }, [properties, portfolioAnalytics])

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Primary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Properties"
          value={stats.totalProperties}
          subtitle={`${stats.recentActivity} added this month`}
          icon={<Home className="w-6 h-6 text-blue-600" />}
        />
        
        <StatsCard
          title="Total Investment"
          value={`$${stats.totalInvestment.toLocaleString()}`}
          subtitle="Purchase + Renovation"
          icon={<DollarSign className="w-6 h-6 text-green-600" />}
        />
        
        <StatsCard
          title="Monthly Cash Flow"
          value={`$${stats.totalCashFlow.toLocaleString()}`}
          subtitle="Across all properties"
          icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
          className={stats.totalCashFlow >= 0 ? '' : 'border-red-200'}
        />
        
        <StatsCard
          title="Average ROI"
          value={`${stats.averageROI.toFixed(1)}%`}
          subtitle="Cash-on-Cash Return"
          icon={<Target className="w-6 h-6 text-orange-600" />}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Analyses"
          value={stats.totalAnalyses || 0}
          subtitle={`${stats.propertiesWithMultipleAnalyses || 0} properties with multiple`}
          icon={<BarChart3 className="w-6 h-6 text-blue-600" />}
        />

        <StatsCard
          title="Total Equity"
          value={`$${stats.totalEquity.toLocaleString()}`}
          subtitle="Down payments invested"
          icon={<PieChart className="w-6 h-6 text-indigo-600" />}
        />

        <StatsCard
          title="Average Cap Rate"
          value={`${stats.averageCapRate.toFixed(1)}%`}
          subtitle="Net Operating Income"
          icon={<TrendingUp className="w-6 h-6 text-teal-600" />}
        />

        <StatsCard
          title="Locations"
          value={stats.locations.size}
          subtitle="Cities & States"
          icon={<MapPin className="w-6 h-6 text-rose-600" />}
        />
      </div>

      {/* Analysis Type Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Analysis Types</h3>
          <div className="text-sm text-gray-500">
            Total: {stats.totalAnalyses || 0} analyses
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div
            className="text-center p-4 rounded-lg bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors cursor-pointer group"
            title={`${stats.analysisTypes['buy-hold']} Buy & Hold analyses across portfolio`}
          >
            <div className="text-2xl font-bold text-blue-600">
              {stats.analysisTypes['buy-hold']}
            </div>
            <div className="text-sm text-gray-600">Buy & Hold</div>
            <div className="text-xs text-gray-500">
              {(stats.totalAnalyses || 0) > 0
                ? ((stats.analysisTypes['buy-hold'] / (stats.totalAnalyses || 1)) * 100).toFixed(0)
                : 0}% of analyses
            </div>
            <div className="text-xs text-blue-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Click for details
            </div>
          </div>

          <div
            className="text-center p-4 rounded-lg bg-green-50 border border-green-100 hover:bg-green-100 transition-colors cursor-pointer group"
            title={`${stats.analysisTypes.flip} Fix & Flip analyses across portfolio`}
          >
            <div className="text-2xl font-bold text-green-600">
              {stats.analysisTypes.flip}
            </div>
            <div className="text-sm text-gray-600">Fix & Flip</div>
            <div className="text-xs text-gray-500">
              {(stats.totalAnalyses || 0) > 0
                ? ((stats.analysisTypes.flip / (stats.totalAnalyses || 1)) * 100).toFixed(0)
                : 0}% of analyses
            </div>
            <div className="text-xs text-green-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Click for details
            </div>
          </div>

          <div
            className="text-center p-4 rounded-lg bg-purple-50 border border-purple-100 hover:bg-purple-100 transition-colors cursor-pointer group"
            title={`${stats.analysisTypes.brrr} BRRR analyses across portfolio`}
          >
            <div className="text-2xl font-bold text-purple-600">
              {stats.analysisTypes.brrr}
            </div>
            <div className="text-sm text-gray-600">BRRR</div>
            <div className="text-xs text-gray-500">
              {(stats.totalAnalyses || 0) > 0
                ? ((stats.analysisTypes.brrr / (stats.totalAnalyses || 1)) * 100).toFixed(0)
                : 0}% of analyses
            </div>
            <div className="text-xs text-purple-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Click for details
            </div>
          </div>
        </div>

        {/* Additional Analytics */}
        {(stats.totalAnalyses || 0) > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Recent Activity:</span>
                <span className="font-medium">{stats.recentActivity} added (30 days)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg per Property:</span>
                <span className="font-medium">
                  {stats.totalProperties > 0
                    ? ((stats.totalAnalyses || 0) / stats.totalProperties).toFixed(1)
                    : '0'} analyses
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Helper function to calculate monthly mortgage payment
